import React, { FunctionComponent, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import useTheme from '@material-ui/core/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery';
import Modal from '../presentation/Modal';
import { PostCodeContext } from '../../react/PostCodeContext';
import PostcodeForm from './postcodeForm/PostcodeForm';
import {
  DEFAULT_DEALER_FILE,
  DEFAULT_DEALER_POSTCODE,
  IDealerDetails,
  IDealers,
} from '../../dealerList/dealersConstants';
import postcodeCheckStyles from './postcodeCheckStyles';
import DealerSelect from './DealerSelect/DealerSelect';
import { IParamState, useUrlPersistentPostcode } from '../../react/useUrlPersistentPostcode';
import Loader from '../presentation/Loader';
import usePlatformConfig from '../../react/usePlatformConfig';
import numberUtilities from '../../../../../common/utilities/numberUtilities';
import logger from '../../service/logger';
import { ISpcTheme } from '../../theme/ISpcTheme';
import errorHandler from '../../service/errorHandler';
import Translation from '../translation/Translation';
import useCustomerStorage from '../../hooks/useCustomerStorage';

export interface IFormData {
  postcode: string;
}

export interface IDealerData {
  dealerInformation: IDealerDetails;
}

interface IUserData {
  postcode?: string;
  dealerInformation?: any;
  dealerId?: string;
  wasSaved?: boolean;
}

interface IProps {
  handleDealerSelectionCallbacks?: Array<() => void>;
}

const PostcodeCheckContainer: FunctionComponent<IProps> = ({ handleDealerSelectionCallbacks }) => {
  const styles = postcodeCheckStyles();
  const theme = useTheme<ISpcTheme>();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const {
    platformConfig: { spcDealersFileName },
  } = usePlatformConfig();
  const { setUrlPostcode, getUrlPostcode } = useUrlPersistentPostcode();
  const { param: paramsPostcode }: IParamState = getUrlPostcode();
  const { customerStorage } = useCustomerStorage();
  const localUserData: any = customerStorage.getCustomerData();
  const userData = useRef<IUserData>({
    postcode: '',
    dealerInformation: null,
    dealerId: '',
    wasSaved: false,
  });
  const [dealers, setDealers] = useState(null);
  const [showResults, setShowResults] = useState<boolean>(false);
  const { isModalOpen, openModal, displayCloseIcon } = useContext(PostCodeContext);
  const [isLoadingResults, setIsLoadingResults] = useState<boolean>(false);
  const [isDealerSearchError, setIsDealerSearchError] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      // Cleans up customer data.
      customerStorage.removeOldCustomerData();
    };
  }, []);

  const onDealerSearchError = useCallback((hasError: boolean) => {
    setIsDealerSearchError(hasError);
  }, []);

  const handleFormSubmit: (formData: IFormData) => void = useCallback(
    formData => {
      setShowResults(false);
      setIsLoadingResults(true);
      const postcode = formData.postcode.toString();
      userData.current.postcode = postcode;
      handleDealerSearch(postcode, dealers);
      setShowResults(true);
    },
    [dealers],
  );

  const handleFormValidationError: () => void = useCallback(() => {
    setShowResults(false);
  }, []);

  const handleDealerSelection: (data?: any[]) => void = useCallback(data => {
    const dealerId = data[0]?.externalId;
    userData.current.wasSaved = true;
    userData.current.dealerId = dealerId;
    persistUserData(userData);
    openModal(false);
    setShowResults(false);

    if (handleDealerSelectionCallbacks) {
      handleDealerSelectionCallbacks.forEach(callback => {
        callback();
      });
    }
  }, []);

  const handleDealerSearch: (dealerPostCode: string, dealersObject: IDealers) => IDealerDetails = (
    dealerPostCode,
    dealersObject,
  ) => {
    if (!spcDealersFileName) {
      logger.warn(`SPC Flag not defined -> spcDealersFileName. Using default dealers`);
    }
    let closestDealerPostCode = dealerPostCode;

    if (!dealersObject[spcDealersFileName || DEFAULT_DEALER_FILE][closestDealerPostCode]) {
      const currentPostCodeList = Object.keys(dealersObject[spcDealersFileName || DEFAULT_DEALER_FILE]);
      const currentNumberPostCodeList = numberUtilities.toNumberArray(currentPostCodeList);
      closestDealerPostCode = numberUtilities.getClosestNumber(currentNumberPostCodeList, dealerPostCode).toString();
      closestDealerPostCode = numberUtilities.convertToPostcodeDigit(closestDealerPostCode);
    }

    const selectedDealer: IDealerDetails =
      dealersObject[spcDealersFileName || DEFAULT_DEALER_FILE][closestDealerPostCode] ??
      dealersObject[spcDealersFileName || DEFAULT_DEALER_FILE][DEFAULT_DEALER_POSTCODE];

    userData.current.dealerInformation = selectedDealer;

    return selectedDealer;
  };

  const persistUserData: (userData: any) => any = () => {
    const { postcode, dealerInformation, dealerId, wasSaved } = userData.current;
    customerStorage.setCustomerData({
      postcode,
      selectedDealer: dealerInformation,
      dealerId,
    });
    setUrlPostcode(postcode, wasSaved);
  };

  useEffect(() => {
    import(/* webpackChunkName: "dealer-list" */ '../../dealerList/dealers')
      .then(module => {
        setDealers(module.default);
      })
      .catch(errorHandler.promise());
  }, []);

  // Check on load for persistent user info and update accordingly
  useEffect(() => {
    if (!dealers) {
      return;
    }

    if (paramsPostcode && paramsPostcode !== localUserData?.postcode && typeof paramsPostcode === 'string') {
      const dealer: IDealerDetails = handleDealerSearch(paramsPostcode, dealers);
      customerStorage.setCustomerData({
        postcode: paramsPostcode,
        selectedDealer: dealer,
      });
    } else if (localUserData?.postcode && !paramsPostcode) {
      setUrlPostcode(localUserData.postcode, !paramsPostcode);
    }
  }, [dealers]);

  const closeModal = useCallback(() => {
    openModal(false);
    setShowResults(false);
  }, [openModal, setShowResults]);

  return (
    <Modal
      open={isModalOpen}
      labelledby={'postcode-check'}
      fullScreen={false}
      hideCloseIcon={!displayCloseIcon}
      data-test={'modal:postcode'}
      onClose={closeModal}
    >
      {!dealers ? (
        <Loader />
      ) : (
        <Box className={isMobile ? styles.modalWrapperMobile : styles.modalWrapper}>
          <Box>
            <Typography
              data-test={'dealer:modal:title'}
              className={` ${styles.title} ${isMobile ? styles.mobileTitle : ''}`}
              variant="h4"
            >
              <Translation id="spc.dealer.modal.title" />
            </Typography>
            <Typography
              data-test={'dealer:modal:description'}
              className={` ${styles.description} ${isMobile ? styles.mobileDescription : ''}`}
              variant="subtitle1"
            >
              <Translation id="spc.dealer.modal.description" />
            </Typography>
          </Box>
          <PostcodeForm
            handleFormSubmission={handleFormSubmit}
            onDealerSearchError={onDealerSearchError}
            handleFormValidationError={handleFormValidationError}
            isDealerSearchError={isDealerSearchError}
          />
          {isLoadingResults && <Loader />}
          {showResults && (
            <DealerSelect
              dealerInformation={userData.current.dealerInformation}
              selectDealer={handleDealerSelection}
              onDealerSearchError={onDealerSearchError}
              setIsLoadingResults={setIsLoadingResults}
            />
          )}
        </Box>
      )}
    </Modal>
  );
};

export default PostcodeCheckContainer;
